// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Combobox, Flex, Stack, Text, TextInput, Title, useCombobox } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import type { LoginAuthenticationResponse } from '@medplum/core';
import { getIdentifier, normalizeOperationOutcome } from '@medplum/core';
import type { OperationOutcome, ProjectMembership } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Logo } from '../Logo/Logo';
import { OperationOutcomeAlert } from '../OperationOutcomeAlert/OperationOutcomeAlert';
import { ProjectMembershipLoginOption } from './ProjectLoginOption';
import projectLoginClasses from './ProjectLoginOption.module.css';

export interface ChooseProfileFormProps {
  readonly login: string;
  readonly memberships: ProjectMembership[];
  readonly handleAuthResponse: (response: LoginAuthenticationResponse) => void;
}

const RECENT_PROJECTS_KEY = 'medplum.recentProjects';
const MAX_RECENT_PROJECTS = 10;

export function ChooseProfileForm(props: ChooseProfileFormProps): JSX.Element {
  const medplum = useMedplum();
  const combobox = useCombobox();
  const [search, setSearch] = useState('');
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const [recentProjects, setRecentProjects] = useLocalStorage<Record<string, number>>({
    key: RECENT_PROJECTS_KEY,
    defaultValue: {},
    getInitialValueInEffect: false,
  });

  function filterDisplay(display: string | undefined): boolean {
    return !!display?.toLowerCase()?.includes(search.toLowerCase());
  }

  function filterMembership(membership: ProjectMembership): boolean {
    return (
      filterDisplay(membership.profile?.display) ||
      filterDisplay(membership.project?.display) ||
      filterDisplay(getMembershipLabel(membership))
    );
  }

  function compareMembershipRecency(m1: ProjectMembership, m2: ProjectMembership): number {
    const t1 = (m1.id && recentProjects[m1.id]) || 0;
    const t2 = (m2.id && recentProjects[m2.id]) || 0;
    return t2 - t1;
  }

  function handleValueSelect(membershipId: string): void {
    medplum
      .post<LoginAuthenticationResponse>('auth/profile', {
        login: props.login,
        profile: membershipId,
      })
      .then((response) => {
        setRecentProjects((prev) => {
          const next = { ...prev, [membershipId]: Date.now() };
          const entries = Object.entries(next).sort((a, b) => b[1] - a[1]);
          return Object.fromEntries(entries.slice(0, MAX_RECENT_PROJECTS));
        });
        props.handleAuthResponse(response);
      })
      .catch((err) => setOutcome(normalizeOperationOutcome(err)));
  }

  const options = props.memberships
    .filter(filterMembership)
    .sort(compareMembershipRecency)
    .slice(0, 10)
    .map((item) => (
      <Combobox.Option value={item.id as string} key={item.id} className={projectLoginClasses.interactive}>
        <ProjectMembershipLoginOption membership={item} label={getMembershipLabel(item)} />
      </Combobox.Option>
    ));

  return (
    <Stack gap="0">
      <Flex justify="center" align="center" direction="column" wrap="nowrap">
        <Logo size={32} />
        <Title order={3} py="lg">
          Choose a Project
        </Title>
      </Flex>
      <OperationOutcomeAlert outcome={outcome} mb="lg" />
      <Combobox store={combobox} onOptionSubmit={handleValueSelect}>
        <Combobox.EventsTarget>
          <TextInput
            placeholder="Search"
            value={search}
            mb="md"
            autoFocus
            leftSection={<IconSearch size={16} />}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              combobox.updateSelectedOptionIndex();
            }}
          />
        </Combobox.EventsTarget>

        <div>
          <Combobox.Options style={{ marginLeft: '-10px', marginRight: '-10px', marginBottom: '-10px' }}>
            {options.length > 0 ? options : <Combobox.Empty>Nothing found...</Combobox.Empty>}
          </Combobox.Options>
        </div>
      </Combobox>
      <Text size="sm" ta="center" mt="md">
        <Anchor
          component="button"
          type="button"
          onClick={() => {
            window.location.href = `/signin?project=new&login=${props.login}`;
          }}
        >
          Create a new project
        </Anchor>
      </Text>
    </Stack>
  );
}

function getMembershipLabel(membership: ProjectMembership): string | undefined {
  return getIdentifier(membership, 'https://medplum.com/identifier/label');
}
